import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Paper,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchBusinessUnit,
  createBscGoal,
} from "../../../api/companyApi";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

export default function BscGoalCreatePage() {
  const { businessDomainId, businessUnitId, category } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const config = BSC_CATEGORY_CONFIG[category];

  const [businessUnit, setBusinessUnit] = useState(null);
  const [successFactors, setSuccessFactors] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  const [selectedSf, setSelectedSf] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [form, setForm] = useState({ plannedValue: "", plannedDate: "", deadline: "" });

  useEffect(() => {
    if (!config) return;
    (async () => {
      try {
        const [unit, sfs, existingGoals] = await Promise.all([
          fetchBusinessUnit(businessUnitId),
          config.fetchSuccessFactors(businessDomainId),
          config.fetchGoals(businessUnitId),
        ]);
        setBusinessUnit(unit);
        setSuccessFactors(Array.isArray(sfs) ? sfs : []);
        // 使用済み指標名セットを構築
        const used = new Set(
          (Array.isArray(existingGoals) ? existingGoals : [])
            .map((r) => r.indicator?.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, businessUnitId]);

  const handleSfChange = (sfId) => {
    const sf = successFactors.find((s) => s.successFactorId === sfId);
    setSelectedSf(sf || null);
    setSelectedIndicator(null);
    setIndicators(Array.isArray(sf?.indicators) ? sf.indicators : []);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIndicator) {
      notify.error(t("bsc.indicator") + " " + t("common.selectRequired"));
      return;
    }
    const goal = {
      indicator: selectedIndicator,
      plannedValue: form.plannedValue,
      plannedDate: form.plannedDate ? `${form.plannedDate}T00:00:00.000Z` : null,
      deadline: form.deadline ? `${form.deadline}T00:00:00.000Z` : null,
      goalOwnerId: businessUnitId,
      goalOwnerName: businessUnit?.businessUnitName || "",
      goalOwnerType: "Business",
      createdGoalAt: new Date().toISOString(),
    };
    try {
      await createBscGoal(businessUnitId, goal);
      notify.success(t("message.create.success"));
      navigate(basePath);
    } catch (err) { notify.apiError(err); }
  };

  if (!config) {
    return <Typography color="error">Unknown BSC category: {category}</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t(config.titleKey)} - {t("bsc.goal.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("bsc.successFactor")}</InputLabel>
              <Select
                value={selectedSf?.successFactorId || ""}
                onChange={(e) => handleSfChange(e.target.value)}
                label={t("bsc.successFactor")}
              >
                {successFactors.map((sf) => (
                  <MenuItem key={sf.successFactorId} value={sf.successFactorId}>
                    {sf.successFactorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required disabled={!selectedSf}>
              <InputLabel>{t("bsc.indicator")}</InputLabel>
              <Select
                value={selectedIndicator?.indicatorName || ""}
                onChange={(e) => {
                  const found = indicators.find((i) => i.indicatorName === e.target.value);
                  setSelectedIndicator(found || null);
                }}
                label={t("bsc.indicator")}
              >
                {indicators.map((ind) => {
                  const isUsed = usedIndicatorNames.has(ind.indicatorName);
                  return (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={isUsed}>
                      {ind.indicatorName}{ind.unit ? `（${ind.unit}）` : ""}
                      {isUsed ? `　${t("bsc.indicator.alreadyUsed")}` : ""}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedIndicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: selectedIndicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: selectedIndicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: selectedIndicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: selectedIndicator.description },
                ].filter((row) => row.value).map((row) => (
                  <Box key={row.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{row.label}:</Typography>
                    <Typography variant="body2">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={`${t("bsc.plannedValue")}${selectedIndicator?.unit ? `（${selectedIndicator.unit}）` : ""}`}
              name="plannedValue"
              value={form.plannedValue}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              type="date"
              label={t("bsc.plannedDate")}
              name="plannedDate"
              value={form.plannedDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              type="date"
              label={t("bsc.deadline")}
              name="deadline"
              value={form.deadline}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
